// src/pages/localization/GlossaryPage.tsx
// Terminology glossary — consistent translation of key terms across
// products. See docs/ux/localization-ia.md §2.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n, useLocalization } from '@react/hooks';
import { Alert, Badge, Button, Input, Textarea } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useScope } from '@/hooks/useScope';
import type { GlossaryEntry } from '@core/types/localization';
import { LocalizationModalShell } from '@/features/localization/ModalShell';
import { errMessage, makeEntity } from '@/features/localization/helpers';

export const GlossaryPage = observer(() => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useLocalization();

  const [entries, setEntries] = useState<GlossaryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<GlossaryEntry | null>(null);
  const [deleting, setDeleting] = useState<GlossaryEntry | null>(null);
  const [busy, setBusy] = useState(false);

  const [term, setTerm] = useState('');
  const [definition, setDefinition] = useState('');
  const [doNotTranslate, setDoNotTranslate] = useState(false);

  const load = () => {
    setIsLoading(true);
    setError(null);
    store
      .ListGlossaryEntries()
      .then((res) => setEntries(res.items?.items ?? []))
      .catch((e: unknown) => setError(errMessage(e, t('localization.glossary.loadError'))))
      .finally(() => setIsLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = search.trim()
    ? entries.filter((e) => e.term.toLowerCase().includes(search.trim().toLowerCase()))
    : entries;

  const openDialog = (e: GlossaryEntry | null) => {
    setEditing(e);
    setTerm(e?.term ?? '');
    setDefinition(e?.definition ?? '');
    setDoNotTranslate(e?.do_not_translate ?? false);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!term.trim() || !definition.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (editing?.entity?.id) {
        await store.UpdateGlossaryEntry(editing.entity.id, {
          entry: { ...editing, term: term.trim(), definition: definition.trim(), do_not_translate: doNotTranslate },
        });
      } else {
        await store.CreateGlossaryEntry({
          entity: makeEntity('', 'glossary_entry', scope),
          term: term.trim(),
          definition: definition.trim(),
          do_not_translate: doNotTranslate,
          translations: [],
        });
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      setError(errMessage(e, t('localization.glossary.saveError')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('localization.glossary.title')}</h1>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/localization" className="text-sm text-primary hover:underline">{t('localization.keys.backToKeys')}</Link>
          <Button size="sm" onClick={() => openDialog(null)}>{t('localization.glossary.newEntry')}</Button>
        </div>
      </div>

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('localization.glossary.searchPlaceholder')}
        className="h-9 w-64 rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground"
      />

      {error && <Alert variant="error">{error}</Alert>}
      {isLoading && <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />}
      {!isLoading && filtered.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('localization.glossary.empty')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('localization.glossary.subtitle')}</p>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((e) => (
          <div key={e.entity?.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">{e.term}</span>
                {e.do_not_translate && <Badge variant="warning">{t('localization.glossary.doNotTranslate')}</Badge>}
              </div>
              <p className="mt-0.5 truncate text-sm text-muted-foreground">{e.definition}</p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <Button size="sm" variant="secondary" onClick={() => openDialog(e)}>{t('common.edit')}</Button>
              <Button size="sm" variant="danger" onClick={() => setDeleting(e)}>{t('common.delete')}</Button>
            </div>
          </div>
        ))}
      </div>

      <LocalizationModalShell
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? t('localization.glossary.editEntry') : t('localization.glossary.newEntry')}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setDialogOpen(false)} disabled={busy}>{t('common.cancel')}</Button>
            <Button size="sm" onClick={save} isLoading={busy} disabled={!term.trim() || !definition.trim()}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label={t('localization.glossary.term')} value={term} onChange={(e) => setTerm(e.target.value)} required />
          <Textarea label={t('localization.glossary.definition')} value={definition} onChange={(e) => setDefinition(e.target.value)} rows={3} required />
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={doNotTranslate} onChange={(e) => setDoNotTranslate(e.target.checked)} />
            {t('localization.glossary.doNotTranslate')}
          </label>
        </div>
      </LocalizationModalShell>

      <ConfirmDialog
        open={!!deleting}
        title={t('localization.glossary.deleteTitle')}
        description={t('localization.glossary.deleteDescription')}
        destructive
        isConfirming={busy}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting?.entity?.id) return;
          setBusy(true);
          try {
            await store.DeleteGlossaryEntry(deleting.entity.id);
            setDeleting(null);
            load();
          } catch (e) {
            setError(errMessage(e, t('localization.glossary.deleteError')));
            setDeleting(null);
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
});
